<script>
import { mapActions } from 'vuex';
import i18n from "@/plugins/i18n";

import { quarters, yearlyQuarterValue } from '../../utils/config';

import dynamicTargets from './dynamicTargets.vue';

import SwitchButton from '../../components/SwitchButton.vue';
import InputFieldGroup from './inputs/input-field-group.vue';
import NameBubble from '../../components/name-bubble.vue';
import MetricDiscussions from './discussion/metric-discussions.vue';

export default {
  components: {
    dynamicTargets,
    SwitchButton,
    InputFieldGroup,
    NameBubble,
    MetricDiscussions,
  },

  data() {
    return {
      loading: false,
      sectionLoading: null,

      facilities: [],
      
      officers: [],
      selectedOfficerToFilterBy: null,

      categories: [],
      elements: [],
      targets: [],
      kpis: [],
      metrics: [],
      submetrics: [],

      metric_inputs: [],

      indexOfSelectedGri: 0,

      selectedFacility: null,

      activeCategoryID: 0, // where process starts from //
      selectedElement: null,
      selectedTarget: null,
      selectedKpi: null,
      selectedMetric: null,

      kpiType: "",
      kpiInputId: null,

      focusedInputValue: {},
      focusedYear: null,
      focusedQuarter: null,

      targetDescriptionExpanded: true,
      kpiDescriptionExpanded: true,

      metricsTablesHeight: 400,

      isCompanyBased: false,

      targetHasNoMatch: false,
      targetIsLoading: false,

      targetCollapsed: true,
    }
  },

  computed: {
    years() {
      const year = new Date().getFullYear();
      return [year, year - 1, year - 2];
    },

    extraYears() {
      const year = new Date().getFullYear();
      return [year - 3, year - 4, year - 5];
    },

    quarters() {
      return quarters;
    },

    yearlyQuarter() {
      return yearlyQuarterValue;
    },

    generalColorCode() {
      return this.getColor(this.activeCategoryID);
    },

    disableUsingAsCompanyMetric() {
      return (this.facilities.length === 1);
    },

    selectedFacilityId() {
      if (!this.selectedFacility)
        return 0;

      let facilityId = this.isCompanyBased ? 0 : this.selectedFacility.facility_id;
      if (this.disableUsingAsCompanyMetric)
        facilityId = this.selectedFacility.facility_id;

      return facilityId;
    },

    filteredMetrics() {
      if (!this.selectedOfficerToFilterBy) {
        return this.metrics;
      }

      return this.metrics.filter(x => x.metric_assignments.some(y => y.assigned_users.some(z => z.user_id == this.selectedOfficerToFilterBy.user_id)));
    },
  },

  async mounted() {
    await this.init();
  },

  updated() {
    this.setGRISectionHeight();
  },

  methods: {
    ...mapActions('admin', [
      'getCompanyOfficers',
      'checkHasAssignment',
    ]),

    ...mapActions('KPIsMetrics', [
      'getFacilities',
      'getCategories',
      'getElementsByPriority',
      'getTargetsByElement',
      'getKpisByTarget',
      'getMetricsByKpi',
      'getSubMetricsByMetric',
      'createMetricsInput',
      'updateMetrics',
      'deleteMetricInput',
    ]),

    ...mapActions("supplier", [
      "recalculateBaseYearValue",
      "recalculateReportingYearValue",
      "recalculateCurrentYearValue"
    ]),

    // initial api calls //

    async init() {
      await this.setLoading(true);
      await this._getCompanyOfficers();
      await this._getFacilities();
      await this.handleRouterQuery();
      await this.setLoading(false);
    },

    async _getCompanyOfficers() {
      await this.getCompanyOfficers()
        .then(res => {
          this.officers = res.data;
        })
        .catch(({ response }) => {
          this.$notify('error', response.data.messageName);
        })
        .finally(async () => {
          if (this.$user.role_id > 4) {
            await this._checkHasAssignment();
          }
        })
    },

    async _checkHasAssignment() {
      await this.checkHasAssignment()
        .then(async (res) => {
          if (res.data.value == true) {
            const me = this.officers.find(x => x.user_id === this.$user.user_id);
            if (me)
              await this.selectOfficerToFilterBy(null, me);
          }
        })
        .catch(({ response }) => {
          console.error(response.data.messageName);
        })
    },

    async _getCategories(initial = false) {
      await this.getCategories()
        .then(async res => {
          this.categories = res.data;

          if (initial) {
            await this.selectCategory(res.data[0].sust_category_id);
          }
        })
    },

    async _getElements() {
      await this.getElementsByPriority(this.activeCategoryID)
        .then(res => {
          this.elements = res.data;
        })
    },

    async _getTargetsByElement() {
      await this.setSectionLoading("targets");
      await this.getTargetsByElement(this.selectedElement.sust_element_id)
        .then(res => {
          this.targets = res.data;
        })
        .finally(async () => {
          await this.setSectionLoading(null);
        })
    },

    async _getKpisByTarget() {
      await this.setSectionLoading("kpis");
      await this.getKpisByTarget(this.selectedTarget.sust_target_id)
        .then(res => {
          this.kpis = res.data;
        })
        .finally(async () => {
          await this.setSectionLoading(null);
        })
    },

    async _getMetricsByKpi() {
      await this.setSectionLoading("metrics");
      await this.getMetricsByKpi(this.selectedKpi.sust_kpi_id)
        .then(res => {
          this.metrics = res.data;
        })
        .finally(async () => {
          await this.setSectionLoading(null);
        })
    },

    async _getSubMetricsByMetric() {
      await this.setSectionLoading("submetrics");
      await this.getSubMetricsByMetric(this.selectedMetric.sust_metric_kpi_id)
        .then(async (res) => {
          this.submetrics = res.data.sub_metrics;
          if (res.data.sub_metrics.length > 0) {
            this.selectedMetric.is_company_based = res.data.is_company_based;
          }
        })
        .catch(() => {
          this.submetrics = [];
        })
        .finally(async () => {
          await this.setSectionLoading(null);
        })
    },

    async handleRouterQuery() {
      //TODO: write specific request for this, this is not optimal for both backend and frontend. need to do it quickly though so it's gonna stay for now
      const category = await this.setQueryParam("category");
      const element = await this.setQueryParam("element");
      const target = await this.setQueryParam("target");
      const kpi = await this.setQueryParam("kpi");
      const metric = await this.setQueryParam("metric");
      if (category && element && target && kpi && metric) {
        await this._getCategories();
        await this.selectCategory(Number(category));

        const elementIndex = this.elements.findIndex(x => x.sust_element_id == element);
        if (elementIndex === -1)
          return;

        await this.selectElement(this.elements[elementIndex]);

        const targetIndex = this.targets.findIndex(x => x.sust_target_id == target);
        if (targetIndex === -1)
          return;

        await this.selectTarget(this.targets[targetIndex]);

        const kpiIndex = this.kpis.findIndex(x => x.sust_kpi_id == kpi);
        if (kpiIndex === -1)
          return;

        await this.selectKpi(this.kpis[kpiIndex]);

        const metricIndex = this.metrics.findIndex(x => x.sust_metric_kpi_id == metric);
        if (metricIndex === -1)
          return;

        await this.selectMetric(this.metrics[metricIndex]);

        const comments = await this.setQueryParam("comments");
        if (comments) {
          const el = document.getElementById('comments');
          if (el) {
            el.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }

      } else {
        await this._getCategories(true);
      }
    },

    async _getFacilities() {
      const params = {
        company_id: this.$user.company_id,
        is_deleted: false
      };

      await this.getFacilities(params)
        .then((res) => {
          this.facilities = res.data;
          this.selectedFacility = res.data[0];
        })
        .catch(({ response }) => {
          this.$toast.open({
            message: this.$parseResMessage(response.data.messageName),
            type: "error",
            duration: 5000,
            dismissible: true,
          })
        })
    },

    async createInput(event) {
      const valid = await this.inputController(event.create_value, event.value_type)

      if (!valid) {
        return;
      }

      if (event.sust_sub_metric_kpi_id == null) {
        const params = {
          company_id: this.$user.company_id,
          facility_id: this.selectedFacilityId,
          created_by: this.$user.user_id,
          metric_kpi_id: event.sust_metric_kpi_id,
          year: event.year,
          quarter: event.quarter,
          value: event.create_value
        };

        await this.createMetricsInput(params)
          .then(async (res) => {
            await this.$toast.open({
              message: this.$parseResMessage(res.data.messageName),
              type: "success",
              duration: 5000,
              dismissible: true,
            });

            const ret = await this.pushToInputsList(params, res.data.metrics_input_table_id, event.unit_type);
            if (!ret) {
              console.error('Failed to push to inputs list.');
            }
          })
          .catch(async ({ response }) => {
            await this.$toast.open({
              message: this.$parseResMessage(response.data.messageName),
              type: "error",
              duration: 5000,
              dismissible: true,
            });
          })
        /* .finally(async () => { //TODO: re-enable these later
          this.$refs.dynamicTargets._recalculateAllValues();
          this.$refs.dynamicTargets._recalculateValuesAsCustomer();
        }) */
      } else {
        const params = {
          company_id: this.$user.company_id,
          facility_id: this.selectedFacilityId,
          created_by: this.$user.user_id,
          sub_metric_kpi_id: event.sust_sub_metric_kpi_id,
          year: event.year,
          quarter: event.quarter,
          value: event.create_value
        };

        await this.createMetricsInput(params)
          .then(async (res) => {
            await this.$toast.open({
              message: this.$parseResMessage(res.data.messageName),
              type: "success",
              duration: 5000,
              dismissible: true,
            });

            const ret = await this.pushToInputsList(params, res.data.metrics_input_table_id, event.unit_type);
            if (!ret) {
              console.error('Failed to push to inputs list.');
            }
          })
          .catch(async ({ response }) => {
            await this.$toast.open({
              message: this.$parseResMessage(response.data.messageName),
              type: "error",
              duration: 5000,
              dismissible: true,
            });
          })
        /* .finally(async () => {//TODO: re-enable these later
          this.$refs.dynamicTargets._recalculateAllValues();
          this.$refs.dynamicTargets._recalculateValuesAsCustomer();
        }) */
      }
    },

    async pushToInputsList(body, id, unit_type) {
      if (body.metric_kpi_id) {
        const idx = this.metrics.findIndex(x => x.sust_metric_kpi_id == body.metric_kpi_id);
        if (idx === -1)
          return false;

        this.metrics[idx].company_inputs.push(
          {
            "metrics_input_table_id": id,
            "metric_kpi_id": body.metric_kpi_id,
            "year": body.year,
            "quarter": body.quarter,
            "facility_id": body.facility_id,
            "value": body.value,
            "has_document": false,
            "unit_type": unit_type
          }
        );

        return true;
      } else if (body.sub_metric_kpi_id) {
        const idx = this.submetrics.findIndex(x => x.sust_sub_metric_kpi_id == body.sub_metric_kpi_id);
        if (idx === -1)
          return false;

        this.submetrics[idx].company_inputs.push(
          {
            "metrics_input_table_id": id,
            "sub_metric_kpi_id": body.sub_metric_kpi_id,
            "year": body.year,
            "quarter": body.quarter,
            "facility_id": body.facility_id,
            "value": body.value,
            "has_document": false,
            "unit_type": unit_type
          }
        );

        return true;
      }
    },

    async updateInput(item) {
      const value = item.value;
      const value_type = item.value_type;

      const valid = await this.inputController(value, value_type)

      if (!valid) {
        return;
      }

      let params = {
        company_id: this.$user.company_id,
        metrics_input_table_id: item.metrics_input_table_id,
        year: item.year,
        quarter: item.quarter,
        facility_id: this.selectedFacilityId,
        updated_by: parseInt(this.$user.user_id),
        value: value
      };

      if (this.onSubmetrics) {
        params["sub_metric_kpi_id"] = item.sust_sub_metric_kpi_id;
      } else {
        params["metric_kpi_id"] = item.sust_metric_kpi_id;
      }

      await this.updateMetrics(params)
        .then(res => {
          this.$toast.open({
            message: this.$parseResMessage(res.data.messageName),
            type: "success",
            duration: 5000,
            dismissible: true,
          })
        })
        .catch(({ response }) => {
          this.$toast.open({
            message: this.$parseResMessage(response.data.messageName),
            type: "error",
            duration: 5000,
            dismissible: true,
          })
        })
      /* .finally(async () => {//TODO: re-enable these later
        this.$refs.dynamicTargets._recalculateAllValues();
        this.$refs.dynamicTargets._recalculateValuesAsCustomer();
      }) */
    },

    async _deleteMetricInput(item) {
      const params = {
        metrics_input_table_id: item.metrics_input_table_id,
        deleted_by: this.$user.user_id
      }

      this.deleteMetricInput(params)
        .then(res => {
          this.$toast.open({
            message: this.$parseResMessage(res.data.messageName),
            type: "success",
            duration: 5000,
            dismissible: true,
          });

          if (this.submetrics.length === 0) {
            this.selectedMetric.company_inputs = this.selectedMetric.company_inputs.filter(x => x.metrics_input_table_id !== params.metrics_input_table_id);
          } else {
            const submetricIndex = this.submetrics.findIndex(x => x.sust_sub_metric_kpi_id === item.sub_metric_kpi_id);
            if (submetricIndex !== -1) {
              this.submetrics[submetricIndex].company_inputs = this.submetrics[submetricIndex].company_inputs.filter(x => x.metrics_input_table_id !== params.metrics_input_table_id);
            }
          }
        })
        .catch(({ response }) => {
          this.$toast.open({
            message: this.$parseResMessage(response.data.messageName),
            type: "error",
            duration: 5000,
            dismissible: true,
          });
        })
      /* .finally(async () => {//TODO: re-enable these later
        this.$refs.dynamicTargets._recalculateAllValues();
        this.$refs.dynamicTargets._recalculateValuesAsCustomer();
      }) */
    },

    async inputController(value, value_type) {
      if (value == null || value == "") {
        return;
      }

      if (value == undefined) {
        throw new Error("Undefined value of metric");
      }
      if (value_type == undefined) {
        throw new Error("Undefined value type of metric");
      }

      const integer_regex = /^\d*$/;
      const float_regex = /^\d*(\.\d*)?$/;

      const is_integer = (integer_regex.test(String(value)));
      const is_valid_float = (float_regex.test(String(value)));

      if (value_type == "int" && !is_integer) {
        this.$toast.open({
          message: i18n.t('kpisMetrics.input_validation_errors.integer_only'),
          type: "error",
          duration: 5000,
          dismissible: true,
        })
        return false;
      }

      if (value_type == "float" && !is_valid_float) {
        this.$toast.open({
          message: i18n.t('kpisMetrics.input_validation_errors.no_commas'),
          type: "error",
          duration: 5000,
          dismissible: true,
        })
        return false;
      }

      return true;
    },

    recalculateTargetValues() {
      this.$refs.dynamicTargets._recalculateAllValues();
      this.$refs.dynamicTargets._recalculateValuesAsCustomer();
    },

    async cacheFocusedItem(item, year, quarter = this.yearlyQuarter) {
      await this.setFocusedInputField(item);
      await this.setFocusedYear(year);
      await this.setFocusedQuarter(quarter)
    },

    async setFocusedInputField(item) {
      for (const key in item) {
        if (Object.hasOwnProperty.call(item, key)) {
          this.focusedInputValue[key] = item[key];
        }
      }
    },

    async setFocusedYear(year) {
      this.focusedYear = year;
    },

    async setFocusedQuarter(quarter) {
      this.focusedQuarter = quarter;
    },

    async resetCachedItem() {
      this.focusedInputValue = {};
      this.focusedYear = null;
      this.focusedQuarter = null;
    },

    async revertToCache(value_type) {
      try {
        await this.updateInput(this.focusedInputValue, value_type);
      } catch (error) {
        throw new Error("Error while reverting to the cached value!");
      } finally {
        await this.resetCachedItem();
      }
    },

    // event handlers //

    selectOfficerToFilterBy(_, officer) {
      if (this.selectedOfficerToFilterBy != null && this.selectedOfficerToFilterBy.user_id === officer.user_id) {
        this.selectedOfficerToFilterBy = null;
      } else {
        this.selectedOfficerToFilterBy = officer;
      }
    },

    async selectCategory(id) {
      if (this.activeCategoryID != id) {
        await this.setLoading(true); // category change is more demanding since it triggers an api call //
        await this.setActiveCategoryID(id);
        await this._getElements();
        await this.resetOnCategoryChange();
      }
    },

    async selectElement(element) {
      if (this.selectedElement != element) {
        await this.setSelectedElement(element);
        await this._getTargetsByElement();
        await this.resetOnElementChange();
      }
    },

    async selectTarget(target) {
      if (this.selectedTarget != target) {
        await this.setSelectedTarget(target);
        await this._getKpisByTarget();
        await this.resetOnTargetChange();
      }
    },

    async selectKpi(kpi) {
      if (this.selectedKpi != kpi) {
        await this.setSelectedKpi(kpi);
        await this._getMetricsByKpi();
        await this.resetOnKpiChange();
      }
    },

    async selectMetric(metric, reload = false) {
      if (this.selectedMetric == metric && reload === false)
        return;

      await this.setSelectedMetric(metric);
      await this._getSubMetricsByMetric();
      await this.useAsCompanyMetric(this.selectedMetric.is_company_based)
      await this.griIndexController("reset");
    },

    async clickExpandButton(which) {
      await this.setDescriptionExpanded(which);
    },

    async griIndexController(action, index = 0) {
      if (action == "left" && this.indexOfSelectedGri != 0) {
        this.indexOfSelectedGri--;
      } else if (action == "right" && this.indexOfSelectedGri != this.selectedMetric.sust_gri_indexes.length - 1) {
        this.indexOfSelectedGri++;
      } else if (action == "reset") {
        this.indexOfSelectedGri = 0;
      } else if (action == "set") {
        this.indexOfSelectedGri = index;
      } else {
        throw new Error("Unexpected action at GRI index");
      }
    },

    async hideDescriptionOnScroll(which) {
      if (which == "kpi" && this.kpiDescriptionExpanded == true) {
        await this.setDescriptionExpanded(which);
      } else if (which == "target" && this.targetDescriptionExpanded == true) {
        await this.setDescriptionExpanded(which);
      } else {
        return;
      }
    },

    async handleSubmetricIsCompanyBased() {
      if (this.submetrics) {
        return;
      }
    },

    async useAsCompanyMetric(state) {
      await this.setIsCompanyBased(state);
    },

    async handleClickMetricName(clickedMetric) { // TODO: take a look
      const kpi = this.kpis.find(item => item.sust_kpi_id == clickedMetric.kpi_id);
      if (this.selectedKpi == null || this.selectedKpi.sust_kpi_id != kpi.kpi_id) {
        await this.selectKpi(kpi);
      }

      const metric = this.metrics.find(item => item.sust_metric_kpi_id == clickedMetric.metric_kpi_id);
      if (this.selectedMetric == null || this.selectedMetric.sust_metric_kpi_id != metric.metric_kpi_id) {
        await this.selectMetric(metric);
      }
    },

    async handleTargetHasNoMatch(e) {
      await this.setTargetHasNoMatch(e);
    },

    async handleTargetIsLoading(e) {
      await this.setTargetIsLoading(e);
    },

    async handleTargetCollapsed(bool) {
      await this.setTargetCollapsed(bool);
    },

    // setters //

    async setLoading(isLoading) {
      this.loading = isLoading;
    },

    async setSectionLoading(section) {
      this.sectionLoading = section;
    },

    async setActiveCategoryID(id) {
      this.activeCategoryID = id;
    },

    async setSelectedElement(element) {
      this.selectedElement = element;
    },

    async setSelectedTarget(target) {
      this.selectedTarget = target;
    },

    async setSelectedKpi(kpi) {
      this.selectedKpi = kpi;
    },

    async setSelectedMetric(metric) {
      this.selectedMetric = metric;
    },

    async setQueryParam(param) {
      return this.$route.query[param];
    },

    async setDescriptionExpanded(which) {
      if (which == "kpi") {
        this.kpiDescriptionExpanded = !this.kpiDescriptionExpanded;
      } else if (which == "target") {
        this.targetDescriptionExpanded = !this.targetDescriptionExpanded;
      } else {
        throw new Error("Unexpected item while expanding description!");
      }
    },

    async setIsCompanyBased(bool) {
      this.isCompanyBased = bool;
    },

    async setTargetHasNoMatch(bool) {
      this.targetHasNoMatch = bool;
    },

    async setTargetIsLoading(bool) {
      this.targetIsLoading = bool;
    },

    async setTargetCollapsed(bool) {
      this.targetCollapsed = bool;
    },

    // resetters //

    async resetOnCategoryChange() {
      await this.griIndexController("reset");
      await this.setSelectedMetric(null);
      await this.setSelectedKpi(null);
      await this.setSelectedTarget(null);
      await this.setSelectedElement(null);

      this.targets = [];
      this.kpis = [];
      this.metrics = [];
      this.submetrics = [];

      await this.setLoading(false);
    },

    async resetOnElementChange() {
      await this.griIndexController("reset");
      await this.setSelectedMetric(null);
      await this.setSelectedKpi(null);
      await this.setSelectedTarget(null);

      this.kpis = [];
      this.metrics = [];
      this.submetrics = [];
    },

    async resetOnTargetChange() {
      await this.griIndexController("reset");
      await this.setSelectedMetric(null);
      await this.setSelectedKpi(null);
      await this.setTargetHasNoMatch(false);

      this.metrics = [];
      this.submetrics = [];
    },

    async resetOnKpiChange() {
      await this.griIndexController("reset");
      await this.setSelectedMetric(null);

      this.submetrics = [];
    },

    // utils //

    getColor(id) {
      if (id == 1) {
        return '#fa8a14'
      }
      if (id == 2) {
        return '#44a900'
      }
      if (id == 3) {
        return '#de0222'
      }
      if (id == 4) {
        return '#004578'
      }
    },

    setGRISectionHeight() {
      const element = document.querySelector("#metrics-tables");
      if (element) {
        const height = Number(element.offsetHeight);

        if (height < 420) {
          return 400;
        } else {
          this.metricsTablesHeight = height - 20;
        }
      }
    },
  }
}
</script>

<template>
  <div class="flex-all-centered-col kpi-page-container">

    <loading-spinner v-if="loading" :container="true" :color="generalColorCode || 'var(--sustainfinity-green)'" />

    <div v-if="!loading" class="selection-section flex-all-centered-row">
      <div v-if="officers.length > 0" class="officer-filters-ctr">
        <name-bubble v-for="(officer, idx) in officers" :Words="[officer.firstname, officer.surname]" 
          :key="idx"
          :XonHover="false"
          :Radius="30"
          :PutEmphasis="selectedOfficerToFilterBy == null ? true : (officer.user_id == selectedOfficerToFilterBy.user_id)"
          :style="{transform: `translateX(-${idx * 10}%)`}" 
          @clickBubble="selectOfficerToFilterBy($event, officer)"
          v-sust-tooltip="{ text: `${officer.firstname} ${officer.surname}`, bgColor: 'var(--sustainfinity-navy)' }" 
        />
      </div>

      <div class="categories-tabs-container" :style="{ borderTop: `thick solid ${generalColorCode}` }">
        <div v-for="(category, index) in categories" :key="category.sust_category_id"
          @click="selectCategory(category.sust_category_id)" class="category flex-all-centered-col"
          :class="{ 'selected-category': activeCategoryID === category.sust_category_id }" :style="{
            backgroundColor: activeCategoryID === category.sust_category_id ? getColor(category.sust_category_id) : 'whitesmoke',
            color: getColor(category.sust_category_id),
            zIndex: activeCategoryID === category.sust_category_id ? 5 : 5 - category.sust_category_id,
            transform: `translateX(${index * 85}%)`
          }">
          {{ category.sust_category_name }}
        </div>
      </div>
    </div>

    <div v-if="!loading" class="top-section flex-all-centered-row" style="margin-bottom: 3em;">
      <!-- ELEMENTS -->
      <div class="flex-all-centered-col elements tkm-col small-box-shadow" style="width: 30%;">
        <div class="tkm-element-wrapper flex-all-centered-col" style="overflow-y: auto !important;">
          <div v-for="(item, index) in elements" :key="index" class="tkm-element"
            :class="{ 'selected-element': selectedElement ? selectedElement.sust_element_id === item.sust_element_id : false }"
            @click="selectElement(item)">
            <div class="element-name-ctr">
              {{ item.sust_element_name }}
            </div>

            <div class="element-percentage-ctr" :style="{ border: `thin solid ${generalColorCode}` }">
              <div class="percentage-text">
                {{ item.complete_percentage }}%
              </div>
              <div class="loading-bar"
                :style="{ width: `${item.complete_percentage}%`, backgroundColor: generalColorCode }">
                <!-- filled up by the bar -->
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="target-kpi-metric flex-all-centered-row small-box-shadow">
        <!-- TARGET -->
        <div class="flex-all-centered-col tkm-col">
          <div class="header">
            {{ $t('kpisMetrics.target') }}
          </div>

          <div class="tkm-element-wrapper flex-all-centered-col" style="padding-bottom: 5em;"
            @scroll="hideDescriptionOnScroll('kpi')">
            <loading-spinner class="section-loading-spinner" :color="generalColorCode"
              v-if="sectionLoading === 'targets'" />
            <div v-for="(item, index) in targets" :key="index" class="tkm-element"
              :class="{ 'selected-element': selectedTarget ? selectedTarget.sust_target_id === item.sust_target_id : false }"
              @click="selectTarget(item)">
              <div>
                {{ item.sust_target_name }}
              </div>
            </div>
          </div>

          <div class="description box-shadow-top"
            :class="{ 'folded-description': !kpiDescriptionExpanded, 'expanded-description': kpiDescriptionExpanded }">
            <div class="expand-button flex-all-centered-row" @click="clickExpandButton('kpi')"
              :title="$t('kpisMetrics.show_desc')">
              <i v-if="!kpiDescriptionExpanded" class="fa-solid fa-angles-up"></i>
              <i v-else class="fa-solid fa-angles-down"></i>
            </div>
            <div class="text">
              {{ $t('kpisMetrics.target_desc') }}
            </div>
          </div>
        </div>

        <div class="flex-all-centered-col tkm-col" style="z-index: 99;">
          <div class="header">
            {{ $t('kpisMetrics.kpi') }}
          </div>

          <div class="tkm-element-wrapper flex-all-centered-col" @scroll="hideDescriptionOnScroll('target')">
            <loading-spinner class="section-loading-spinner" :color="generalColorCode"
              v-if="sectionLoading === 'kpis'" />
            <div v-for="(item, index) in kpis" :key="index" class="tkm-element" @click="selectKpi(item)"
              :class="{ 'selected-element': selectedKpi ? selectedKpi.sust_kpi_id === item.sust_kpi_id : false }">
              {{ item.sust_kpi_name }}
            </div>
          </div>

          <div class="description box-shadow-top"
            :class="{ 'folded-description': !targetDescriptionExpanded, 'expanded-description': targetDescriptionExpanded }">
            <div class="expand-button flex-all-centered-row" @click="clickExpandButton('target')"
              :title="$t('kpisMetrics.show_desc')">
              <i v-if="!targetDescriptionExpanded" class="fa-solid fa-angles-up"></i>
              <i v-else class="fa-solid fa-angles-down"></i>
            </div>
            <div class="text">
              {{ $t('kpisMetrics.kpi_desc') }}
            </div>
          </div>
        </div>

        <div class="flex-all-centered-col tkm-col">
          <div class="header">
            {{ $t('kpisMetrics.metric') }}
          </div>

          <div class="tkm-element-wrapper flex-all-centered-col" style="height: 100%; overflow-y: auto !important;">
            <loading-spinner class="section-loading-spinner" :color="generalColorCode"
              v-if="sectionLoading === 'metrics'" />
            <div v-for="(item, index) in filteredMetrics" :key="index" class="tkm-element" @click="selectMetric(item)"
              :class="{ 'selected-element': selectedMetric ? selectedMetric.sust_metric_kpi_id === item.sust_metric_kpi_id : false }">
              {{ item.sust_metric_kpi_name }}
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- TODO: re-enable these later -->
    <!-- <div v-if="!loading && selectedTarget" class="middle-section flex-all-centered-col" style="margin-bottom: 3em;">
        <div v-if="targetIsLoading">
          <loading-spinner :color="generalColorCode || 'var(--sustainfinity-green)'" style="font-size: 2em;"/>
        </div>

        <div class="target-has-no-match flex-all-centered-row" v-if="targetHasNoMatch && !targetIsLoading">
          <div class="warning-icon">
            <i class="fa-solid fa-circle-exclamation"></i>
          </div>
          <div class="text">
            {{ $t('kpisMetrics.input_validation_errors.values_no_match') }}
            <span @click="recalculateTargetValues()">{{ $t('kpisMetrics.input_validation_errors.click_here') }}</span>.
          </div>
        </div>

        <div class="collapse-target flex-all-centered-row" @click="handleTargetCollapsed(false)" v-if="targetCollapsed">
          {{ $t('dynamicTargets.show') }}
          <i class="fa-solid fa-angle-down"></i>
        </div>

        <dynamic-targets 
          ref="dynamicTargets"
          @onClickMetricName="handleClickMetricName" 
          @targetHasNoMatch="handleTargetHasNoMatch"
          @isLoading="handleTargetIsLoading"
          @collapseTarget="handleTargetCollapsed(true)"
          :filteredMetrics="metrics" 
          :selectedTarget="selectedTarget" 
          :generalColorCode="generalColorCode"
          :key="selectedTarget"
          v-show="!targetCollapsed"
        />
      </div>  -->

    <div v-if="!loading && selectedMetric" class="bottom-section small-box-shadow">
      <div class="left flex-all-centered-col">
        <div class="facility-select-ctr" v-if="facilities.length > 1">
          <div class="kpi-page-switch-btn-ctr">
            <switch-button :colorOn="'var(--sustainfinity-navy)'" :colorOff="'var(--sustainfinity-blue)'"
              :thumbColor="'whitesmoke'" :key="selectedMetric.sust_metric_kpi_id" @buttonState="useAsCompanyMetric"
              :initialValue="selectedMetric.is_company_based" /> <!-- key property forces a rerender -->
            <!-- 
                TODO: Big problem with company based submetrics:
                Submetrics are not selected one at a time as metrics are, therefore a single switch button
                for determining whether or not that specific submetric is company based or not
                is impossible.
                Need to work on the idea of individually setting the company 
                based status (data already comes correctly from the backend) with the switch button.
              -->
            <div>
              <span v-if="!isCompanyBased">
                {{ $t('dynamicTargets.use_facility_metric') }}
              </span>
              <span v-else>
                {{ $t('dynamicTargets.use_company_metric') }}
              </span>
            </div>
          </div>

          <select class="facility-selection" v-model="selectedFacility" v-if="!isCompanyBased">
            <option v-for="(item, index) in facilities" :key="index" :value="item">
              {{ item.facility_name }}
            </option>
          </select>
        </div>

        <div v-if="selectedMetric.metric_assignments.find(x => x.facility_id == selectedFacilityId)"
          class="flex-all-centered-row" style="justify-content: flex-start;"
        >
          <name-bubble v-for="(officer, idx) in selectedMetric.metric_assignments.find(x => x.facility_id == selectedFacilityId).assigned_users" 
            :key="idx"
            :Words="[officer.firstname, officer.surname]" :XonHover="false"
            :Radius="30"
            :PutEmphasis="true"
            :style="{transform: `translateX(-${idx * 10}%)`}" 
            v-sust-tooltip="{ text: `${officer.firstname} ${officer.surname}`, bgColor: 'var(--sustainfinity-navy)' }" 
          />
        </div>

        <div class="header">
          {{ selectedMetric.sust_metric_kpi_name }} <span
            v-if="selectedMetric.unit_type && selectedMetric.unit_type !== '-'">({{ selectedMetric.unit_type }})</span>
        </div>

        <div class="description">
          {{ selectedMetric.sust_metric_kpi_description }}
        </div>
      </div>

      <div class="right flex-all-centered-col">
        <div class="top-info flex-all-centered-row" v-if="selectedMetric">
          <div class="element-icons flex-all-centered-row" v-if="selectedMetric.global_goal_images.length > 0">
            <div class="element-icon" v-for="item in selectedMetric.global_goal_images" :key="item">
              <img :src="item">
            </div>
          </div>

          <div class="gris flex-all-centered-row" v-if="selectedMetric.sust_gri_indexes.length > 0">
            <div class="gri" v-for="(item, index) in selectedMetric.sust_gri_indexes" :key="index">
              <span @click="griIndexController('set', index)" :class="{ 'active-gri': indexOfSelectedGri == index }">
                GRI {{ item.sust_gri_index_name }}
                <img src="/images/GRI-Logo.png" class="gri-logo img-fluid" alt="gri-logo">
              </span>
            </div>
          </div>
        </div>

        <div class="bottom-info flex-all-centered-row">
          <div id="metrics-tables" style="width: 100%;">
            <div v-if="submetrics.length === 0" class="flex-all-centered-col"
              style="width: 95%; justify-content: flex-start; align-items: flex-start;">
              <input-field-group v-for="year in years" :key="year" :Metric="selectedMetric" :Year="year"
                :SelectedFacilityId="selectedFacilityId" :GeneralColorCode="generalColorCode"
                @delete="_deleteMetricInput($event)" @update="updateInput($event)" @create="createInput($event)" />

              <input-field-group :Metric="selectedMetric" :Years="extraYears" :SelectedFacilityId="selectedFacilityId"
                :GeneralColorCode="generalColorCode" @delete="_deleteMetricInput($event)" @update="updateInput($event)"
                @create="createInput($event)" />
            </div>

            <div v-else v-for="submetric in submetrics" :key="submetric.sust_sub_metric_kpi_id"
              class="flex-all-centered-col"
              style="width: 95%; justify-content: flex-start; align-items: flex-start; margin-bottom: .5em; border-bottom: thin solid lightgray;">
              <div style="font-size: 1vw; font-weight: bold; padding-bottom: .3em;">
                {{ submetric.sust_sub_metric_kpi_name }} ({{ submetric.unit_type }})
              </div>

              <input-field-group v-for="year in years" :key="year" :Metric="submetric" :Year="year"
                :SelectedFacilityId="selectedFacilityId" :GeneralColorCode="generalColorCode"
                @delete="_deleteMetricInput($event)" @update="updateInput($event)" @create="createInput($event)" />

              <input-field-group :Metric="submetric" :Years="extraYears" :SelectedFacilityId="selectedFacilityId"
                :GeneralColorCode="generalColorCode" @delete="_deleteMetricInput($event)" @update="updateInput($event)"
                @create="createInput($event)" />

            </div>
          </div>

          <div class="right flex-all-centered-col" :style="{ 'max-height': `${metricsTablesHeight}px` }"
            v-if="selectedMetric && selectedMetric.sust_gri_indexes.length > 0">
            <div class="header flex-all-centered-row">
              <div class="flex-all-centered-row arrow-icon">
                <i class="fa-solid fa-arrow-left" :style="{ color: generalColorCode }"
                  @click="griIndexController('left')"></i>
              </div>

              <div class="flex-all-centered-row gri-desc">
                GRI {{ selectedMetric.sust_gri_indexes[indexOfSelectedGri].sust_gri_index_name }}
              </div>

              <div class="flex-all-centered-row arrow-icon">
                <i class="fa-solid fa-arrow-right" :style="{ color: generalColorCode }"
                  @click="griIndexController('right')"></i>
              </div>
            </div>

            <div class="description flex-all-centered-row"
              v-html="selectedMetric.sust_gri_indexes[indexOfSelectedGri].sust_gri_index_disclosure">
            </div>

          </div>
        </div>
      </div>
    </div>

    <div v-if="selectedMetric" class="flex-all-centered-row"
      id="comments"
    >
      <metric-discussions 
        :metric="selectedMetric"
        :customStyle="{
          'main': {
            'width': '100%',
            'color': 'black',
            'background-color': 'whitesmoke',
            'border-radius': '1em',
            'box-shadow': '0 0 .5em .1em gray',
          },
          'title': {
            'color': 'black',
            'background-color': 'whitesmoke',
            'font-size': '1.2em',
            'font-weight': 'bold',
          },
          'textarea': {
            'color': 'black',
          },
          'date-update-data': {
            'color': 'black',
          },
        }"
        :showX="false"
      />
    </div>
  </div>
</template>

<style lang='scss' scoped>
.kpi-page-container {
  position: relative;

  font-size: .8em;

  min-height: 800px;

  justify-content: flex-start;

  >.selection-section {
    justify-content: flex-end;
    align-content: flex-start;
    align-items: flex-start;
    margin-bottom: 15px;

    >.categories-tabs-container {
      height: 40px;
      width: 77%;
      position: relative;
      align-self: flex-start;

      >.category {
        position: absolute;
        top: 0;

        height: 40px;
        width: 27.5%;
        font-weight: bold; // temporary //

        border-bottom-right-radius: 150px;
        border-bottom-left-radius: 150px;


        border: .3em solid #d4dddf;
        border-top: 0;

        background-color: whitesmoke;

        transition: all 150ms;
        cursor: pointer;

        &:hover {
          filter: brightness(110%);
        }
      }

      >.selected-category {
        z-index: 99 !important;
        border: 0;
        color: white !important;
      }
    }

  }
}

.general-container {
  // controls the common css of all the containers //
  margin-bottom: 30px;

  background-color: whitesmoke;
  border-radius: 1em;
  box-shadow: 0 0 .5em .1em gray;

  font-size: 1.1em;
}

.top-section {
  justify-content: flex-start;
  align-items: stretch;

  height: 500px;

  >.target-kpi-metric {
    margin-left: 30px;

    border-radius: 1em;
    overflow: hidden;
  }
}

.middle-section {
  width: 100%;

  >.target-has-no-match {
    color: whitesmoke;
    background-color: rgba(3, 132, 171, 0.8);
    padding: .5em;
    border-radius: 6px;
    margin-bottom: 1em;

    >.warning-icon {
      font-size: 2em;
      padding-right: .5em;
    }

    >.text>span {
      text-decoration: underline;
      transition: all 100ms;
      cursor: pointer;

      &:hover {
        color: var(--sustainfinity-navy);
      }
    }
  }

  >.collapse-target {
    margin-bottom: 1em;
    background-color: whitesmoke;
    padding: .5em;
    border-radius: .5em;
    box-shadow: 0 .1em .5em .1em gray;

    transition: all 150ms;
    cursor: pointer;

    &:hover {
      background-color: #d4dddf;
    }

    >i {
      line-height: 1.5;
      padding-left: .25em;
    }
  }
}

.elements {
  border-radius: 1em;
}

.tkm-col {
  justify-content: flex-start;
  align-items: stretch;
  box-sizing: content-box;
  overflow: hidden;
  height: 500px;
  border-right: thin solid lightgray;

  >.tkm-element-wrapper {
    justify-content: flex-start;
    align-items: stretch;
    overflow-x: hidden;
    overflow-y: scroll;
    box-sizing: content-box;
    height: 600px;
    background-color: whitesmoke;

    &::-webkit-scrollbar {
      border-top-right-radius: 1em;
      border-bottom-right-radius: 1em;
      margin-right: 100px;
      width: 12px !important;
    }

    &::-webkit-scrollbar-track {
      margin-right: 1em;
      background: var(--sustainfinity-navy);
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 1em;
      background: lightgray !important;
    }

    >.tkm-element {
      padding: 1em 2em 1em 2em !important;
      border-bottom: thin solid lightgray;
      height: fit-content;
      /* max-height: 150px; */
      font-size: 1.1em;
      background-color: whitesmoke;
      color: var(--sustainfinity-navy);
      transition: all 100ms;
      cursor: pointer;

      &:hover {
        background-color: var(--background-fade);
      }

      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-content: flex-start;
      align-items: flex-start;

      &:last-of-type {
        border-bottom: 0;
      }

      >.element-percentage-ctr {
        height: 15px;
        width: 100%;
        border-radius: 1em;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-content: center;
        align-items: center;
        background-color: white;
        overflow: hidden;
        position: relative;

        >.percentage-text {
          width: 100%;
          height: 100%;
          text-align: center;
          position: absolute;
          font-size: .66em;
          color: black !important;
          transform: translateX(2.5%);
        }

        >.loading-bar {
          height: 100%;
        }
      }
    }

    >.target-element {

      display: flex;
      flex-direction: row;
      justify-content: space-around;
      align-content: center;
      align-items: center;

      cursor: default;

      &:hover {
        background-color: whitesmoke;
      }

      >.target-add-button {
        display: flex;
        justify-content: center;
        align-content: center;
        align-items: center;

        margin-left: 3px;

        background-color: rgb(227, 227, 227);
        min-width: 40px;
        min-height: 40px;
        border-radius: 40px;

        cursor: pointer;

        &:hover {
          background-color: lightgray;
        }
      }
    }

    >.selected-element {
      background-color: var(--sustainfinity-navy);
      color: whitesmoke;
      pointer-events: none;
    }
  }

  .header {
    padding: .35em 1em .35em 1.3em;
    font-size: 1.5em;
    font-weight: bold;
    background-color: whitesmoke;
    color: black;
    border-bottom: thin solid lightgray;
  }

  .description {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    background-color: whitesmoke;
    color: black;
    border-top: thin solid lightgray;
    font-size: .85em;
    font-weight: bold;
    /* min-height: 200px;
      max-height: 200px; */

    display: flex;
    align-items: center;

    position: absolute;
    transition: all 300ms ease-in;

    >.expand-button {
      width: 100%;
      border-bottom: thin solid lightgray;
      padding: .5em;
      min-height: 30px;
      max-height: 30px;

      transition: all 100ms;
      cursor: pointer;

      &:hover {
        background-color: rgb(231, 231, 231);
      }
    }

    >.text {
      padding: .5em 1em .5em 1em;
    }
  }

  .folded-description {
    top: calc(100% - 30px);
  }

  .expanded-description {
    top: calc(100%);
    transform: translateY(-100%);
  }

  position: relative;
}

.bottom-section {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-content: flex-start;
  align-items: stretch;

  margin-bottom: 20px;
  background-color: whitesmoke;
  border-radius: 1em;

  >.left {
    color: black;
    padding: 1em;
    width: 60%;
    border-right: medium solid #d4dddf;

    justify-content: flex-start;

    >.header {
      width: 100%;
      font-size: 1.5em;
      font-weight: bold;
      padding-bottom: .5em;
    }

    >.description {
      width: 100%;
      font-size: .95em;
    }
  }

  >.right {
    justify-content: flex-start;
    align-content: flex-start;

    padding-left: 1em;

    >.top-info {
      justify-content: space-between;

      padding: 1em 0 1em 0;

      >.gris {
        max-width: 250px;
        flex-wrap: wrap;
        justify-content: flex-end;
        padding-right: 1em;

        >.gri:first-of-type {
          >span {
            padding-right: 0 !important;
          }
        }

        >.gri>span {
          color: black !important;
          padding-right: .5em;

          cursor: pointer;

          &:hover {
            text-decoration: underline;
          }


          >.gri-logo {
            max-width: 1.33em;
            transform: translateY(-10%);
          }
        }

        >.gri>.active-gri {
          text-decoration: underline !important;
          font-style: italic;
          pointer-events: none !important;
        }
      }

      >.element-icons {
        max-width: 250px;
        flex-wrap: wrap;
        justify-content: flex-start;

        >.element-icon>img {
          max-width: 50px;
          margin-right: 2px;
        }
      }
    }

    >.bottom-info {
      justify-content: space-between;
      align-items: flex-start;

      >.right {
        border: thin solid var(--sustainfinity-navy);
        border-radius: 1em;
        overflow: auto;

        margin-right: 10px;
        justify-content: flex-start;
        flex-basis: 60%;
        margin-bottom: 10px;

        max-width: 350px;

        >.header {
          justify-content: center;
          padding: 5px 15px 5px 15px;
          width: 100%;
          border-bottom: thin solid var(--sustainfinity-navy);
          font-weight: bold;

          >.arrow-icon {
            width: 10px;

            >i {
              height: fit-content;
              transition: 100ms;
              cursor: pointer;

              &:hover {
                filter: brightness(80%);
              }
            }
          }

          >.gri-desc {
            justify-content: flex-start;
            padding-left: 1em;
          }
        }

        >.description {
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-content: flex-start;
          align-items: flex-start;
          overflow: auto;
          padding: 5px 15px 5px 15px;
        }
      }
    }
  }

}

.facility-select-ctr {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-content: center;
  align-items: center;
  width: 100%;
  padding-bottom: 1em;

  >.kpi-page-switch-btn-ctr {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;

    >div>span {
      font-weight: bold;
    }
  }
}

.facility-selection {
  border: 0;
  border-bottom: thin solid var(--sustainfinity-navy);
  outline: 0;
  background: 0;
  padding: .33em .66em .33em .66em;
  margin-left: 1em;

  cursor: pointer;

  &:focus {
    outline: thin solid var(--sustainfinity-navy);
  }
}

.box-shadow-top {
  box-shadow: 0 .33em 1em .2em gray;
}

.unmatching-values-body {
  >p:nth-of-type(2) {
    font-weight: bold;
    font-size: 1.1;
  }

  >.unmatching-values-buttons {
    justify-content: flex-end !important;

    >button {
      margin-left: .5em;
      border-radius: .33em;
      padding: .33em .76em .33em .76em;
      border: 0;
      outline: 0;

      transition: all 150ms;

      &:hover {
        filter: brightness(115%);
      }

      &:focus {
        outline: thin solid darkgray !important;
      }

      &:active {
        outline: .125em solid darkgray !important;
      }
    }
  }
}

.officer-filters-ctr {
  min-width: 22% !important;
  max-width: 22% !important;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
}

.section-loading-spinner {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  font-size: 2.5vw;
  margin: 2vw 0;
}

#comments {
  animation: Flash 1s forwards;
}

@keyframes Flash {
  0% {
    opacity: 1;
  } 50% {
    opacity: 0.2;
  } 100% {
    opacity: 1;
  }
}

@media screen and (max-width: 1920px) {
  .tkm-col>.description {
    min-height: 12em;
    max-height: 12em;
  }
}

@media screen and (max-width: 1450px) {
  .tkm-col>.description {
    min-height: 14em;
    max-height: 14em;
  }
}

/* @media screen and (max-width: 1400px) {
    .tkm-col > .description {
      min-height: 13.5em;
      max-height: 13.5em;
    }
  } */

@media screen and (max-width: 1366px) {
  .tkm-col>.description {
    min-height: 16em;
    max-height: 16em;
  }
}

@media screen and (max-width: 1300px) {
  .tkm-col>.description {
    min-height: 17em;
    max-height: 17em;
  }
}

@media screen and (max-width: 1210px) {
  .tkm-col>.description {
    min-height: 18.5em;
    max-height: 18.5em;
  }
}

@media screen and (max-width: 1125px) {
  .tkm-col>.description {
    min-height: 20em;
    max-height: 20em;
  }
}
</style>